import React, { useState } from 'react';
import { QuoteData } from '../../App';
import { FileTextIcon, EyeIcon, EyeOffIcon, CalculatorIcon, PlusCircleIcon, EditIcon, CheckCircleIcon, BuildingIcon, UserIcon, ChevronDownIcon, RefreshCwIcon, XIcon, PercentIcon, DollarSignIcon, LayoutIcon, ChevronRightIcon } from 'lucide-react';
interface QuotePreviewProps {
  quoteData: QuoteData;
  setQuoteData: (data: QuoteData) => void;
}
type ViewMode = 'dealer' | 'customer';
interface PageVisibility {
  coverPage: boolean;
  clientInfo: boolean;
  partsBreakdown: boolean;
  pivotsInfo: boolean;
  transportInfo: boolean;
  pricingSummary: boolean;
  termsConditions: boolean;
}
export function QuotePreview({
  quoteData,
  setQuoteData
}: QuotePreviewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('dealer');
  const [showPageControls, setShowPageControls] = useState(false);
  const [showExtrasModal, setShowExtrasModal] = useState(false);
  const [showAlterationsModal, setShowAlterationsModal] = useState(false);
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['pricingSummary']));
  const [pageVisibility, setPageVisibility] = useState<PageVisibility>({
    coverPage: true,
    clientInfo: true,
    partsBreakdown: true,
    pivotsInfo: true,
    transportInfo: true,
    pricingSummary: true,
    termsConditions: true
  });
  // Extras state
  const [extraName, setExtraName] = useState('');
  const [extraAmount, setExtraAmount] = useState('');
  const [extras, setExtras] = useState<Array<{
    id: string;
    name: string;
    amount: number;
  }>>([]);
  // Alterations state
  const [discount, setDiscount] = useState('0');
  const [margin, setMargin] = useState('0');
  const [customAdjustment, setCustomAdjustment] = useState('0');
  const quoteId = 'B8TF0001';
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  // Calculate totals
  const partsSubtotal = quoteData.parts.reduce((sum, part) => sum + part.quantity * part.unitPrice, 0);
  const transportTotal = quoteData.transport;
  const insuranceTotal = quoteData.insurance;
  const grandTotal = partsSubtotal + transportTotal + insuranceTotal;
  // Mock dealer discount and margin for demo
  const dealerDiscount = 10; // 10%
  const dealerMargin = 15; // 15%
  const dealerPrice = partsSubtotal * (1 - dealerDiscount / 100);
  const customerPrice = dealerPrice * (1 + dealerMargin / 100);
  const handleAddExtra = () => {
    if (extraName && extraAmount) {
      const newExtra = {
        id: Date.now().toString(),
        name: extraName,
        amount: parseFloat(extraAmount)
      };
      setExtras([...extras, newExtra]);
      setExtraName('');
      setExtraAmount('');
    }
  };
  const handleRemoveExtra = (id: string) => {
    setExtras(extras.filter(extra => extra.id !== id));
  };
  const handleRecalculate = () => {
    // Trigger a recalculation animation
    const message = `Quote recalculated successfully!\n\nNew Total: ${quoteData.currency} $${grandTotal.toFixed(2)}`;
    alert(message);
  };
  const handleSaveAlterations = () => {
    alert(`Alterations saved:\n- Discount: ${discount}%\n- Margin: ${margin}%\n- Custom Adjustment: ${quoteData.currency} $${customAdjustment}`);
    setShowAlterationsModal(false);
  };
  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };
  const togglePageVisibility = (page: keyof PageVisibility) => {
    setPageVisibility(prev => ({
      ...prev,
      [page]: !prev[page]
    }));
  };
  const getVisiblePages = () => {
    return Object.entries(pageVisibility).filter(([_, visible]) => visible).length;
  };
  const PageFooter = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="mt-8 pt-4 border-t border-gray-300 flex items-center justify-between text-xs text-gray-600" data-id="element-1113">
      <span data-id="element-1114">Generated by Aisco Workspace</span>
      <span data-id="element-1115">Quote #{quoteId}</span>
      <span data-id="element-1116">
        Page {pageNumber} of {totalPages}
      </span>
    </div>;
  const CoverPage = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="bg-white rounded-xl shadow-lg p-12 mb-6 min-h-[1000px] flex flex-col" data-id="element-1117">
      <div className="flex-1 flex flex-col items-center justify-center text-center" data-id="element-1118">
        <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center mb-8 shadow-xl" data-id="element-1119">
          <FileTextIcon className="w-12 h-12 text-white" data-id="element-1120" />
        </div>
        <h1 className="text-5xl font-bold text-gray-900 mb-4" data-id="element-1121">QUOTATION</h1>
        <div className="w-32 h-1 bg-blue-600 rounded-full mb-8" data-id="element-1122"></div>
        <h2 className="text-3xl font-semibold text-gray-700 mb-12" data-id="element-1123">
          {quoteData.name || 'Irrigation Equipment Quote'}
        </h2>
        <div className="space-y-4 text-lg text-gray-600" data-id="element-1124">
          <div className="flex items-center justify-center gap-3" data-id="element-1125">
            <span className="font-semibold text-gray-900" data-id="element-1126">Quote Number:</span>
            <span className="font-mono bg-blue-50 px-4 py-2 rounded-lg" data-id="element-1127">
              {quoteId}
            </span>
          </div>
          <div className="flex items-center justify-center gap-3" data-id="element-1128">
            <span className="font-semibold text-gray-900" data-id="element-1129">Date:</span>
            <span data-id="element-1130">{currentDate}</span>
          </div>
          <div className="flex items-center justify-center gap-3" data-id="element-1131">
            <span className="font-semibold text-gray-900" data-id="element-1132">Currency:</span>
            <span data-id="element-1133">{quoteData.currency}</span>
          </div>
          {quoteData.quoteType === 'dealer-customer' && <div className="flex items-center justify-center gap-3" data-id="element-1134">
              <span className="font-semibold text-gray-900" data-id="element-1135">Quote Type:</span>
              <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-lg font-semibold" data-id="element-1136">
                {viewMode === 'dealer' ? 'Dealer Quote' : 'Customer Quote'}
              </span>
            </div>}
        </div>
      </div>
      <PageFooter pageNumber={pageNumber} totalPages={totalPages} data-id="element-1137" />
    </div>;
  const ClientInfoPage = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="bg-white rounded-xl shadow-lg p-12 mb-6 min-h-[1000px] flex flex-col" data-id="element-1138">
      <div className="flex-1" data-id="element-1139">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-gray-300" data-id="element-1140">
          Client Information
        </h2>
        <div className="grid grid-cols-2 gap-8" data-id="element-1141">
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200" data-id="element-1142">
            <div className="flex items-center gap-3 mb-4" data-id="element-1143">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center" data-id="element-1144">
                <BuildingIcon className="w-6 h-6 text-white" data-id="element-1145" />
              </div>
              <h3 className="text-xl font-bold text-gray-900" data-id="element-1146">
                Company Details
              </h3>
            </div>
            <div className="space-y-3" data-id="element-1147">
              <div data-id="element-1148">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1149">
                  Company Name
                </p>
                <p className="text-base font-semibold text-gray-900" data-id="element-1150">
                  Aisco Workspace
                </p>
              </div>
              <div data-id="element-1151">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1152">
                  Address
                </p>
                <p className="text-sm text-gray-700" data-id="element-1153">
                  123 Industrial Avenue
                  <br data-id="element-1154" />
                  Madrid, Spain 28001
                </p>
              </div>
              <div data-id="element-1155">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1156">
                  Contact
                </p>
                <p className="text-sm text-gray-700" data-id="element-1157">
                  +34 91 123 4567
                  <br data-id="element-1158" />
                  contact@aisco.com
                </p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 rounded-xl p-6 border border-green-200" data-id="element-1159">
            <div className="flex items-center gap-3 mb-4" data-id="element-1160">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center" data-id="element-1161">
                <UserIcon className="w-6 h-6 text-white" data-id="element-1162" />
              </div>
              <h3 className="text-xl font-bold text-gray-900" data-id="element-1163">
                {viewMode === 'dealer' ? 'Dealer' : 'Customer'} Information
              </h3>
            </div>
            <div className="space-y-3" data-id="element-1164">
              <div data-id="element-1165">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1166">
                  Name
                </p>
                <p className="text-base font-semibold text-gray-900" data-id="element-1167">
                  {viewMode === 'dealer' ? 'North Region Distributors' : 'ABC Farm Corporation'}
                </p>
              </div>
              <div data-id="element-1168">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1169">
                  Address
                </p>
                <p className="text-sm text-gray-700" data-id="element-1170">
                  {viewMode === 'dealer' ? '123 Industrial Ave, Chicago, USA' : '100 Farm Road, Springfield, USA'}
                </p>
              </div>
              <div data-id="element-1171">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1172">
                  Contact
                </p>
                <p className="text-sm text-gray-700" data-id="element-1173">
                  {viewMode === 'dealer' ? '+1 (555) 123-4567' : '+1 (555) 111-2222'}
                  <br data-id="element-1174" />
                  {viewMode === 'dealer' ? 'contact@northregion.com' : 'john@abcfarm.com'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PageFooter pageNumber={pageNumber} totalPages={totalPages} data-id="element-1175" />
    </div>;
  const PartsBreakdownPage = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="bg-white rounded-xl shadow-lg p-12 mb-6 min-h-[1000px] flex flex-col" data-id="element-1176">
      <div className="flex-1" data-id="element-1177">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-gray-300" data-id="element-1178">
          Parts Breakdown
        </h2>
        <table className="w-full" data-id="element-1179">
          <thead data-id="element-1180">
            <tr className="bg-gray-100 border-b-2 border-gray-300" data-id="element-1181">
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-1182">
                Part Name
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-1183">
                Part Number
              </th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700" data-id="element-1184">
                Qty
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700" data-id="element-1185">
                Unit Price
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700" data-id="element-1186">
                Total
              </th>
            </tr>
          </thead>
          <tbody data-id="element-1187">
            {quoteData.parts.map(part => <tr key={part.id} className="border-b border-gray-200" data-id="element-1188">
                <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1189">{part.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600" data-id="element-1190">
                  {part.partNumber}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 text-center" data-id="element-1191">
                  {part.quantity}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1192">
                  {quoteData.currency} ${part.unitPrice.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-1193">
                  {quoteData.currency} $
                  {(part.quantity * part.unitPrice).toFixed(2)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <PageFooter pageNumber={pageNumber} totalPages={totalPages} data-id="element-1194" />
    </div>;
  const PivotsInfoPage = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="bg-white rounded-xl shadow-lg p-12 mb-6 min-h-[1000px] flex flex-col" data-id="element-1195">
      <div className="flex-1" data-id="element-1196">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-gray-300" data-id="element-1197">
          Pivot Systems
        </h2>
        {quoteData.pivots.length > 0 ? <div className="space-y-6" data-id="element-1198">
            {quoteData.pivots.map(pivot => <div key={pivot.id} className="bg-blue-50 rounded-xl p-6 border border-blue-200" data-id="element-1199">
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1200">
                  {pivot.name}
                </h3>
                <div className="grid grid-cols-3 gap-4" data-id="element-1201">
                  <div data-id="element-1202">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1203">
                      Model
                    </p>
                    <p className="text-base font-semibold text-gray-900" data-id="element-1204">
                      {pivot.model}
                    </p>
                  </div>
                  <div data-id="element-1205">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1206">
                      Length
                    </p>
                    <p className="text-base font-semibold text-gray-900" data-id="element-1207">
                      {pivot.length}
                    </p>
                  </div>
                  <div data-id="element-1208">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1209">
                      Area
                    </p>
                    <p className="text-base font-semibold text-gray-900" data-id="element-1210">
                      {pivot.area}
                    </p>
                  </div>
                  <div data-id="element-1211">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1212">
                      Flow
                    </p>
                    <p className="text-base font-semibold text-gray-900" data-id="element-1213">
                      {pivot.flow}
                    </p>
                  </div>
                  <div data-id="element-1214">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1215">
                      Angle
                    </p>
                    <p className="text-base font-semibold text-gray-900" data-id="element-1216">
                      {pivot.angle}
                    </p>
                  </div>
                  <div data-id="element-1217">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1218">
                      Quantity
                    </p>
                    <p className="text-base font-semibold text-gray-900" data-id="element-1219">
                      {pivot.quantity}
                    </p>
                  </div>
                </div>
              </div>)}
          </div> : <p className="text-gray-500 text-center py-12" data-id="element-1220">
            No pivot systems included in this quote
          </p>}
      </div>
      <PageFooter pageNumber={pageNumber} totalPages={totalPages} data-id="element-1221" />
    </div>;
  const TransportInfoPage = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="bg-white rounded-xl shadow-lg p-12 mb-6 min-h-[1000px] flex flex-col" data-id="element-1222">
      <div className="flex-1" data-id="element-1223">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-gray-300" data-id="element-1224">
          Transport & Logistics
        </h2>
        <div className="space-y-6" data-id="element-1225">
          <div className="bg-gray-50 rounded-xl p-6" data-id="element-1226">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1227">
              Delivery Information
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-1228">
              <div data-id="element-1229">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1230">
                  Incoterm
                </p>
                <p className="text-base font-semibold text-gray-900" data-id="element-1231">
                  {quoteData.incoterm}
                </p>
              </div>
              <div data-id="element-1232">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1233">
                  Delivery Date
                </p>
                <p className="text-base font-semibold text-gray-900" data-id="element-1234">
                  {quoteData.deliveryDate || 'To be confirmed'}
                </p>
              </div>
            </div>
          </div>
          {quoteData.transportRoutes.length > 0 && <div data-id="element-1235">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1236">
                Transport Routes
              </h3>
              <table className="w-full" data-id="element-1237">
                <thead data-id="element-1238">
                  <tr className="bg-gray-100 border-b-2 border-gray-300" data-id="element-1239">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-1240">
                      From
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-1241">
                      To
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-1242">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700" data-id="element-1243">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1244">
                  {quoteData.transportRoutes.map(route => <tr key={route.id} className="border-b border-gray-200" data-id="element-1245">
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1246">
                        {route.from}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1247">
                        {route.to}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1248">
                        {route.type}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-1249">
                        {quoteData.currency} $
                        {(route.quantity * route.unitPrice).toFixed(2)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
      <PageFooter pageNumber={pageNumber} totalPages={totalPages} data-id="element-1250" />
    </div>;
  const PricingSummaryPage = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="bg-white rounded-xl shadow-lg p-12 mb-6 min-h-[1000px] flex flex-col" data-id="element-1251">
      <div className="flex-1" data-id="element-1252">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-gray-300" data-id="element-1253">
          Pricing Summary
        </h2>
        <div className="space-y-6" data-id="element-1254">
          <div className="bg-gray-50 rounded-xl p-8" data-id="element-1255">
            <div className="space-y-4" data-id="element-1256">
              <div className="flex justify-between text-lg" data-id="element-1257">
                <span className="font-medium text-gray-700" data-id="element-1258">
                  Parts Subtotal:
                </span>
                <span className="font-semibold text-gray-900" data-id="element-1259">
                  {quoteData.currency} ${partsSubtotal.toFixed(2)}
                </span>
              </div>
              {quoteData.quoteType === 'dealer-customer' && viewMode === 'dealer' && <>
                    <div className="flex justify-between text-lg text-green-700" data-id="element-1260">
                      <span className="font-medium" data-id="element-1261">
                        Dealer Discount ({dealerDiscount}%):
                      </span>
                      <span className="font-semibold" data-id="element-1262">
                        - {quoteData.currency} $
                        {(partsSubtotal * (dealerDiscount / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="pt-4 border-t border-gray-300" data-id="element-1263">
                      <div className="flex justify-between text-xl" data-id="element-1264">
                        <span className="font-bold text-gray-900" data-id="element-1265">
                          Dealer Price:
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-1266">
                          {quoteData.currency} ${dealerPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </>}
              {quoteData.quoteType === 'dealer-customer' && viewMode === 'customer' && <>
                    <div className="flex justify-between text-lg text-blue-700" data-id="element-1267">
                      <span className="font-medium" data-id="element-1268">
                        Dealer Margin ({dealerMargin}%):
                      </span>
                      <span className="font-semibold" data-id="element-1269">
                        + {quoteData.currency} $
                        {(dealerPrice * (dealerMargin / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="pt-4 border-t border-gray-300" data-id="element-1270">
                      <div className="flex justify-between text-xl" data-id="element-1271">
                        <span className="font-bold text-gray-900" data-id="element-1272">
                          Customer Price:
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-1273">
                          {quoteData.currency} ${customerPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </>}
              <div className="flex justify-between text-lg" data-id="element-1274">
                <span className="font-medium text-gray-700" data-id="element-1275">Transport:</span>
                <span className="font-semibold text-gray-900" data-id="element-1276">
                  {quoteData.currency} ${transportTotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-lg" data-id="element-1277">
                <span className="font-medium text-gray-700" data-id="element-1278">Insurance:</span>
                <span className="font-semibold text-gray-900" data-id="element-1279">
                  {quoteData.currency} ${insuranceTotal.toFixed(2)}
                </span>
              </div>
              <div className="pt-6 border-t-2 border-gray-400" data-id="element-1280">
                <div className="flex justify-between text-3xl" data-id="element-1281">
                  <span className="font-bold text-gray-900" data-id="element-1282">TOTAL:</span>
                  <span className="font-bold text-blue-600" data-id="element-1283">
                    {quoteData.currency} $
                    {((quoteData.quoteType === 'dealer-customer' && viewMode === 'customer' ? customerPrice : quoteData.quoteType === 'dealer-customer' && viewMode === 'dealer' ? dealerPrice : partsSubtotal) + transportTotal + insuranceTotal).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PageFooter pageNumber={pageNumber} totalPages={totalPages} data-id="element-1284" />
    </div>;
  const TermsConditionsPage = ({
    pageNumber,
    totalPages
  }: {
    pageNumber: number;
    totalPages: number;
  }) => <div className="bg-white rounded-xl shadow-lg p-12 mb-6 min-h-[1000px] flex flex-col" data-id="element-1285">
      <div className="flex-1" data-id="element-1286">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-gray-300" data-id="element-1287">
          Terms & Conditions
        </h2>
        <div className="space-y-6 text-sm text-gray-700" data-id="element-1288">
          <div data-id="element-1289">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1290">
              1. Validity
            </h3>
            <p data-id="element-1291">This quotation is valid for 30 days from the date of issue.</p>
          </div>
          <div data-id="element-1292">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1293">
              2. Payment Terms
            </h3>
            <p data-id="element-1294">
              Payment terms are Net 30 days from invoice date. Late payments may
              incur interest charges.
            </p>
          </div>
          <div data-id="element-1295">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1296">
              3. Delivery
            </h3>
            <p data-id="element-1297">
              Delivery times are estimates and may vary based on product
              availability and shipping conditions.
            </p>
          </div>
          <div data-id="element-1298">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1299">
              4. Warranty
            </h3>
            <p data-id="element-1300">
              All products are covered by manufacturer warranty. Specific
              warranty terms vary by product.
            </p>
          </div>
          <div data-id="element-1301">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1302">5. Returns</h3>
            <p data-id="element-1303">
              Returns must be authorized and made within 30 days of delivery.
              Restocking fees may apply.
            </p>
          </div>
        </div>
      </div>
      <PageFooter pageNumber={pageNumber} totalPages={totalPages} data-id="element-1304" />
    </div>;
  const visiblePages = getVisiblePages();
  return <div className="w-full space-y-6" data-id="element-1305">
      {/* Control Panel - New Modern Design */}
      <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden backdrop-blur-sm" data-id="element-1306">
        {/* Header Section */}
        <div className="relative overflow-hidden" data-id="element-1307">
          {/* Decorative background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-indigo-600/5 to-purple-600/5" data-id="element-1308"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-full blur-3xl" data-id="element-1309"></div>
          <div className="relative p-6" data-id="element-1310">
            <div className="flex items-start justify-between gap-6" data-id="element-1311">
              {/* Left - Title and Description */}
              <div className="flex items-start gap-4 flex-1" data-id="element-1312">
                <div className="flex-1" data-id="element-1313">
                  <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1314">
                    Quote Preview
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1315">
                    Review your quote document, customize visibility, and
                    prepare for delivery
                  </p>
                </div>
              </div>
              {/* Right - View Mode Toggle */}
              {quoteData.quoteType === 'dealer-customer' && <div className="flex gap-2 bg-white p-1.5 rounded-xl shadow-inner border border-gray-200" data-id="element-1316">
                  <button onClick={() => setViewMode('dealer')} className={`relative flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold transition-all ${viewMode === 'dealer' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-600/30' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1317">
                    <BuildingIcon className="w-4 h-4" data-id="element-1318" />
                    <span className="text-sm" data-id="element-1319">Dealer</span>
                    {viewMode === 'dealer' && <CheckCircleIcon className="w-4 h-4 ml-1" data-id="element-1320" />}
                  </button>
                  <button onClick={() => setViewMode('customer')} className={`relative flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold transition-all ${viewMode === 'customer' ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/30' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1321">
                    <UserIcon className="w-4 h-4" data-id="element-1322" />
                    <span className="text-sm" data-id="element-1323">Customer</span>
                    {viewMode === 'customer' && <CheckCircleIcon className="w-4 h-4 ml-1" data-id="element-1324" />}
                  </button>
                </div>}
            </div>
          </div>
        </div>
        {/* Quick Actions Section */}
        <div className="border-t border-gray-200/50 bg-gradient-to-br from-gray-50 to-blue-50/30" data-id="element-1325">
          <div className="p-6" data-id="element-1326">
            <div className="grid grid-cols-4 gap-3" data-id="element-1327">
              <button onClick={() => setShowExtrasModal(true)} className="group relative overflow-hidden bg-white rounded-xl border-2 border-gray-200 hover:border-blue-600 shadow-sm hover:shadow-xl transition-all duration-300 p-3" data-id="element-1328">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1329"></div>
                <div className="relative" data-id="element-1330">
                  <div className="flex items-center gap-2.5" data-id="element-1331">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 group-hover:from-blue-600 group-hover:to-blue-700 flex items-center justify-center transition-all duration-300 shadow-md group-hover:shadow-lg group-hover:scale-110" data-id="element-1332">
                      <PlusCircleIcon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" data-id="element-1333" />
                    </div>
                    <div className="text-left flex-1" data-id="element-1334">
                      <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors" data-id="element-1335">
                        Add Extras
                      </h4>
                      <p className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors mt-0.5" data-id="element-1336">
                        Include additional items
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-1337"></div>
              </button>
              <button onClick={() => setShowAlterationsModal(true)} className="group relative overflow-hidden bg-white rounded-xl border-2 border-gray-200 hover:border-blue-600 shadow-sm hover:shadow-xl transition-all duration-300 p-3" data-id="element-1338">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1339"></div>
                <div className="relative" data-id="element-1340">
                  <div className="flex items-center gap-2.5" data-id="element-1341">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 group-hover:from-blue-600 group-hover:to-blue-700 flex items-center justify-center transition-all duration-300 shadow-md group-hover:shadow-lg group-hover:scale-110" data-id="element-1342">
                      <EditIcon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" data-id="element-1343" />
                    </div>
                    <div className="text-left flex-1" data-id="element-1344">
                      <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors" data-id="element-1345">
                        Edit Alterations
                      </h4>
                      <p className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors mt-0.5" data-id="element-1346">
                        Adjust discounts & margins
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-1347"></div>
              </button>
              <button onClick={handleRecalculate} className="group relative overflow-hidden bg-white rounded-xl border-2 border-gray-200 hover:border-blue-600 shadow-sm hover:shadow-xl transition-all duration-300 p-3" data-id="element-1348">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1349"></div>
                <div className="relative" data-id="element-1350">
                  <div className="flex items-center gap-2.5" data-id="element-1351">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 group-hover:from-blue-600 group-hover:to-blue-700 flex items-center justify-center transition-all duration-300 shadow-md group-hover:shadow-lg group-hover:scale-110" data-id="element-1352">
                      <RefreshCwIcon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors group-hover:rotate-180" data-id="element-1353" />
                    </div>
                    <div className="text-left flex-1" data-id="element-1354">
                      <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors" data-id="element-1355">
                        Recalculate
                      </h4>
                      <p className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors mt-0.5" data-id="element-1356">
                        Update totals & pricing
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-1357"></div>
              </button>
              <button onClick={() => setShowDocumentPanel(!showDocumentPanel)} className={`group relative overflow-hidden bg-white rounded-xl border-2 shadow-sm hover:shadow-xl transition-all duration-300 p-3 ${showDocumentPanel ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-600'}`} data-id="element-1358">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1359"></div>
                <div className="relative" data-id="element-1360">
                  <div className="flex items-center gap-2.5" data-id="element-1361">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300 shadow-md group-hover:shadow-lg group-hover:scale-110 ${showDocumentPanel ? 'from-blue-600 to-blue-700' : 'from-blue-100 to-blue-200 group-hover:from-blue-600 group-hover:to-blue-700'}`} data-id="element-1362">
                      <LayoutIcon className={`w-5 h-5 transition-colors ${showDocumentPanel ? 'text-white' : 'text-blue-600 group-hover:text-white'}`} data-id="element-1363" />
                    </div>
                    <div className="text-left flex-1" data-id="element-1364">
                      <h4 className={`text-sm font-bold transition-colors ${showDocumentPanel ? 'text-blue-700' : 'text-gray-900 group-hover:text-blue-700'}`} data-id="element-1365">
                        Document Layout
                      </h4>
                      <p className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors mt-0.5" data-id="element-1366">
                        Customize page visibility
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-1367"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content Area with Side Panel */}
      <div className="flex gap-6" data-id="element-1368">
        {/* Document Sections Panel - Now on the LEFT */}
        {showDocumentPanel && <div className="w-80 flex-shrink-0" data-id="element-1369">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 sticky top-6" data-id="element-1370">
              <div className="flex flex-col max-h-[calc(100vh-8rem)]" data-id="element-1371">
                {/* Header */}
                <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100 rounded-t-xl" data-id="element-1372">
                  <div className="flex items-center justify-between mb-3" data-id="element-1373">
                    <div className="flex items-center gap-3" data-id="element-1374">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md" data-id="element-1375">
                        <LayoutIcon className="w-4 h-4 text-white" data-id="element-1376" />
                      </div>
                      <div data-id="element-1377">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-1378">
                          Document Pages
                        </h3>
                      </div>
                    </div>
                    <button onClick={() => setShowDocumentPanel(false)} className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors" title="Close panel" data-id="element-1379">
                      <XIcon className="w-5 h-5 text-gray-600" data-id="element-1380" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1381">
                    {getVisiblePages()} of 7 pages visible
                  </p>
                </div>
                {/* Pages List */}
                <div className="flex-1 overflow-y-auto p-5 space-y-1" data-id="element-1382">
                  {/* Cover Page */}
                  <div className="select-none" data-id="element-1383">
                    <div className="flex items-center gap-2 py-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1384">
                      <label className="flex items-center gap-3 flex-1 cursor-pointer px-2" data-id="element-1385">
                        <input type="checkbox" checked={pageVisibility.coverPage} onChange={() => togglePageVisibility('coverPage')} className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1386" />
                        <span className={`text-sm font-medium ${pageVisibility.coverPage ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1387">
                          Cover Page
                        </span>
                      </label>
                    </div>
                  </div>
                  {/* Client Info */}
                  <div className="select-none" data-id="element-1388">
                    <div className="flex items-center gap-2 py-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1389">
                      <label className="flex items-center gap-3 flex-1 cursor-pointer px-2" data-id="element-1390">
                        <input type="checkbox" checked={pageVisibility.clientInfo} onChange={() => togglePageVisibility('clientInfo')} className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1391" />
                        <span className={`text-sm font-medium ${pageVisibility.clientInfo ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1392">
                          Client Information
                        </span>
                      </label>
                    </div>
                  </div>
                  {/* Parts Breakdown */}
                  <div className="select-none" data-id="element-1393">
                    <div className="flex items-center gap-2 py-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1394">
                      <label className="flex items-center gap-3 flex-1 cursor-pointer px-2" data-id="element-1395">
                        <input type="checkbox" checked={pageVisibility.partsBreakdown} onChange={() => togglePageVisibility('partsBreakdown')} className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1396" />
                        <span className={`text-sm font-medium ${pageVisibility.partsBreakdown ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1397">
                          Parts Breakdown
                        </span>
                      </label>
                    </div>
                  </div>
                  {/* Pivots Info */}
                  <div className="select-none" data-id="element-1398">
                    <div className="flex items-center gap-2 py-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1399">
                      <label className="flex items-center gap-3 flex-1 cursor-pointer px-2" data-id="element-1400">
                        <input type="checkbox" checked={pageVisibility.pivotsInfo} onChange={() => togglePageVisibility('pivotsInfo')} className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1401" />
                        <span className={`text-sm font-medium ${pageVisibility.pivotsInfo ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1402">
                          Pivot Systems
                        </span>
                      </label>
                    </div>
                  </div>
                  {/* Transport Info */}
                  <div className="select-none" data-id="element-1403">
                    <div className="flex items-center gap-2 py-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1404">
                      <label className="flex items-center gap-3 flex-1 cursor-pointer px-2" data-id="element-1405">
                        <input type="checkbox" checked={pageVisibility.transportInfo} onChange={() => togglePageVisibility('transportInfo')} className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1406" />
                        <span className={`text-sm font-medium ${pageVisibility.transportInfo ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1407">
                          Transport & Logistics
                        </span>
                      </label>
                    </div>
                  </div>
                  {/* Pricing Summary with sub-items */}
                  <div className="select-none" data-id="element-1408">
                    <div className="flex items-center gap-2 py-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1409">
                      <button onClick={() => toggleSection('pricingSummary')} className="p-1 hover:bg-blue-200 rounded transition-colors" data-id="element-1410">
                        {expandedSections.has('pricingSummary') ? <ChevronDownIcon className="w-4 h-4 text-gray-600" data-id="element-1411" /> : <ChevronRightIcon className="w-4 h-4 text-gray-600" data-id="element-1412" />}
                      </button>
                      <label className="flex items-center gap-3 flex-1 cursor-pointer" data-id="element-1413">
                        <input type="checkbox" checked={pageVisibility.pricingSummary} onChange={() => togglePageVisibility('pricingSummary')} className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1414" />
                        <span className={`text-sm font-medium ${pageVisibility.pricingSummary ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1415">
                          Pricing Summary
                        </span>
                      </label>
                    </div>
                    {expandedSections.has('pricingSummary') && pageVisibility.pricingSummary && <div className="ml-8 space-y-1 mt-1" data-id="element-1416">
                          <div className="flex items-center gap-3 py-1.5 px-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1417">
                            <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1418" />
                            <span className="text-xs text-gray-700" data-id="element-1419">
                              Parts Subtotal
                            </span>
                          </div>
                          <div className="flex items-center gap-3 py-1.5 px-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1420">
                            <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1421" />
                            <span className="text-xs text-gray-700" data-id="element-1422">
                              Transport
                            </span>
                          </div>
                          <div className="flex items-center gap-3 py-1.5 px-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1423">
                            <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1424" />
                            <span className="text-xs text-gray-700" data-id="element-1425">
                              Insurance
                            </span>
                          </div>
                          <div className="flex items-center gap-3 py-1.5 px-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1426">
                            <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1427" />
                            <span className="text-xs text-gray-700" data-id="element-1428">
                              Grand Total
                            </span>
                          </div>
                        </div>}
                  </div>
                  {/* Terms & Conditions */}
                  <div className="select-none" data-id="element-1429">
                    <div className="flex items-center gap-2 py-2 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1430">
                      <label className="flex items-center gap-3 flex-1 cursor-pointer px-2" data-id="element-1431">
                        <input type="checkbox" checked={pageVisibility.termsConditions} onChange={() => togglePageVisibility('termsConditions')} className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-1432" />
                        <span className={`text-sm font-medium ${pageVisibility.termsConditions ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1433">
                          Terms & Conditions
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* Document Preview */}
        <div className={`transition-all duration-300 ${showDocumentPanel ? 'flex-1' : 'w-full'}`} data-id="element-1434">
          <div className="bg-gray-100 rounded-xl p-8" data-id="element-1435">
            <div className="max-w-4xl mx-auto space-y-6" data-id="element-1436">
              {pageVisibility.coverPage && <CoverPage pageNumber={1} totalPages={visiblePages} data-id="element-1437" />}
              {pageVisibility.clientInfo && <ClientInfoPage pageNumber={1 + (pageVisibility.coverPage ? 1 : 0)} totalPages={visiblePages} data-id="element-1438" />}
              {pageVisibility.partsBreakdown && quoteData.parts.length > 0 && <PartsBreakdownPage pageNumber={1 + (pageVisibility.coverPage ? 1 : 0) + (pageVisibility.clientInfo ? 1 : 0)} totalPages={visiblePages} data-id="element-1439" />}
              {pageVisibility.pivotsInfo && quoteData.pivots.length > 0 && <PivotsInfoPage pageNumber={1 + (pageVisibility.coverPage ? 1 : 0) + (pageVisibility.clientInfo ? 1 : 0) + (pageVisibility.partsBreakdown && quoteData.parts.length > 0 ? 1 : 0)} totalPages={visiblePages} data-id="element-1440" />}
              {pageVisibility.transportInfo && <TransportInfoPage pageNumber={1 + (pageVisibility.coverPage ? 1 : 0) + (pageVisibility.clientInfo ? 1 : 0) + (pageVisibility.partsBreakdown && quoteData.parts.length > 0 ? 1 : 0) + (pageVisibility.pivotsInfo && quoteData.pivots.length > 0 ? 1 : 0)} totalPages={visiblePages} data-id="element-1441" />}
              {pageVisibility.pricingSummary && <PricingSummaryPage pageNumber={1 + (pageVisibility.coverPage ? 1 : 0) + (pageVisibility.clientInfo ? 1 : 0) + (pageVisibility.partsBreakdown && quoteData.parts.length > 0 ? 1 : 0) + (pageVisibility.pivotsInfo && quoteData.pivots.length > 0 ? 1 : 0) + (pageVisibility.transportInfo ? 1 : 0)} totalPages={visiblePages} data-id="element-1442" />}
              {pageVisibility.termsConditions && <TermsConditionsPage pageNumber={visiblePages} totalPages={visiblePages} data-id="element-1443" />}
            </div>
          </div>
        </div>
      </div>
      {/* Add Extras Modal */}
      {showExtrasModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1444">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1445">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 relative" data-id="element-1446">
              <button onClick={() => setShowExtrasModal(false)} className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-lg transition-colors" data-id="element-1447">
                <XIcon className="w-5 h-5" data-id="element-1448" />
              </button>
              <h2 className="text-2xl font-bold text-white mb-2" data-id="element-1449">Add Extras</h2>
              <p className="text-blue-100" data-id="element-1450">
                Add additional items or costs to your quote
              </p>
            </div>
            <div className="p-6 space-y-6" data-id="element-1451">
              <div className="space-y-4" data-id="element-1452">
                <div data-id="element-1453">
                  <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-1454">
                    Extra Name
                  </label>
                  <input type="text" value={extraName} onChange={e => setExtraName(e.target.value)} placeholder="e.g., Installation Service" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1455" />
                </div>
                <div data-id="element-1456">
                  <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-1457">
                    Amount ({quoteData.currency})
                  </label>
                  <input type="number" value={extraAmount} onChange={e => setExtraAmount(e.target.value)} placeholder="0.00" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1458" />
                </div>
                <button onClick={handleAddExtra} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold" data-id="element-1459">
                  <PlusCircleIcon className="w-5 h-5" data-id="element-1460" />
                  Add Extra
                </button>
              </div>
              {extras.length > 0 && <div className="space-y-3" data-id="element-1461">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-1462">
                    Added Extras
                  </h3>
                  {extras.map(extra => <div key={extra.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-1463">
                      <div data-id="element-1464">
                        <p className="font-semibold text-gray-900" data-id="element-1465">
                          {extra.name}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-1466">
                          {quoteData.currency} ${extra.amount.toFixed(2)}
                        </p>
                      </div>
                      <button onClick={() => handleRemoveExtra(extra.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1467">
                        <XIcon className="w-5 h-5" data-id="element-1468" />
                      </button>
                    </div>)}
                </div>}
              <button onClick={() => setShowExtrasModal(false)} className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold" data-id="element-1469">
                Done
              </button>
            </div>
          </div>
        </div>}
      {/* Edit Alterations Modal */}
      {showAlterationsModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1470">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full" data-id="element-1471">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 relative" data-id="element-1472">
              <button onClick={() => setShowAlterationsModal(false)} className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-lg transition-colors" data-id="element-1473">
                <XIcon className="w-5 h-5" data-id="element-1474" />
              </button>
              <h2 className="text-2xl font-bold text-white mb-2" data-id="element-1475">
                Edit Alterations
              </h2>
              <p className="text-indigo-100" data-id="element-1476">
                Adjust discounts, margins, and custom adjustments
              </p>
            </div>
            <div className="p-6 space-y-6" data-id="element-1477">
              <div className="space-y-4" data-id="element-1478">
                <div data-id="element-1479">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1480">
                    <PercentIcon className="w-4 h-4 text-green-600" data-id="element-1481" />
                    Discount (%)
                  </label>
                  <input type="number" value={discount} onChange={e => setDiscount(e.target.value)} placeholder="0" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-1482" />
                </div>
                <div data-id="element-1483">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1484">
                    <PercentIcon className="w-4 h-4 text-blue-600" data-id="element-1485" />
                    Margin (%)
                  </label>
                  <input type="number" value={margin} onChange={e => setMargin(e.target.value)} placeholder="0" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-1486" />
                </div>
                <div data-id="element-1487">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1488">
                    <DollarSignIcon className="w-4 h-4 text-purple-600" data-id="element-1489" />
                    Custom Adjustment ({quoteData.currency})
                  </label>
                  <input type="number" value={customAdjustment} onChange={e => setCustomAdjustment(e.target.value)} placeholder="0.00" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-1490" />
                </div>
              </div>
              <div className="flex gap-3" data-id="element-1491">
                <button onClick={handleSaveAlterations} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold" data-id="element-1492">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-1493" />
                  Save Changes
                </button>
                <button onClick={() => setShowAlterationsModal(false)} className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold" data-id="element-1494">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}